import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";
import { ErrorNote, Spinner } from "@/components/ui";
import type { UseQueryResult } from "@tanstack/react-query";

interface Props<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
  query: UseQueryResult<unknown>;
  total: number;
  page: number;
  pageCount: number;
  onPage: (updater: (p: number) => number) => void;
  empty?: string;
  toolbar?: ReactNode;
}

export function PagedTable<T>({
  columns,
  data,
  query,
  total,
  page,
  pageCount,
  onPage,
  empty,
  toolbar,
}: Props<T>) {
  const { t } = useTranslation();
  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <div className="space-y-3">
      {toolbar}
      {query.isLoading && <Spinner />}
      {query.isError && <ErrorNote message={(query.error as Error).message} />}

      {!query.isLoading && !query.isError && (
        <>
          <div className="overflow-x-auto nm-card">
            <table className="nm-table w-full text-sm">
              <thead className="border-b border-olive-600 bg-olive-700 text-left">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((h) => (
                      <th
                        key={h.id}
                        className="px-4 py-2 font-semibold text-sand"
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-olive-700 last:border-0 hover:bg-olive-700/50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
                {table.getRowModel().rows.length === 0 && (
                  <tr>
                    <td
                      className="px-4 py-6 text-center text-sand-muted"
                      colSpan={columns.length}
                    >
                      {empty ?? t("common.noData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-sand-muted">
            <span>{t("common.total", { count: total })}</span>
            <div className="flex items-center gap-2">
              <button
                className="rounded border border-olive-600 px-2 py-1 hover:text-sand disabled:opacity-40"
                onClick={() => onPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                {t("common.prev")}
              </button>
              <span>
                {page + 1} / {pageCount}
              </span>
              <button
                className="rounded border border-olive-600 px-2 py-1 hover:text-sand disabled:opacity-40"
                onClick={() => onPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page + 1 >= pageCount}
              >
                {t("common.next")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
